import { showToast } from '@/helper/toast.helper';
import { AppDispatch, RootState } from '@/store';
import { fetchFaqById, updateFaq } from '@/store/slices/faqsManagementSlice/faqSlice';
import { FaqStatus } from '@/types/faq.types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { InputField } from '../CommonComponents/InputField';
import { Stepper } from '../CommonComponents/Stepper';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

const EditFaqForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = useState(1);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [isConfirmed, setIsConfirmed] = useState(false);

  const [questionError, setQuestionError] = useState('');
  const [answerError, setAnswerError] = useState('');
  const [showPopup, setShowPopup] = useState(false);

  const { item } = useSelector((state: RootState) => state.faq);

  useEffect(() => {
    const selectedId = Number(id);
    if (selectedId) {
      dispatch(fetchFaqById(selectedId));
    } else {
      navigate('/dashboard/faqs');
    }
  }, [id, dispatch, navigate]);

  useEffect(() => {
    if (item) {
      setQuestion(item.question || '');
      setAnswer(item.answer || '');
      setStatus(item.status === FaqStatus.ACTIVE ? 'Active' : 'Inactive');
    }
  }, [item]);

  if (!item) return <p>No FAQ found.</p>;

  const validateQuestion = (value: string) => {
    if (!value.trim()) {
      return 'Question is required.';
    }
    if (value.length < 5) {
      return 'Question must be 5–300 characters.';
    }
    if (value.length > 300) {
      return 'Question must be 5–300 characters.';
    }
    return '';
  };

  const validateAnswer = (value: string) => {
    if (!value.trim()) {
      return 'Answer is required.';
    }
    if (value.length < 10) {
      return 'Answer must be 10–600 characters.';
    }
    if (value.length > 600) {
      return 'Answer must be 10–600 characters.';
    }
    return '';
  };

  const handleReset = () => {
    if (item) {
      setQuestion(item.question || '');
      setAnswer(item.answer || '');
      setStatus(item.status === FaqStatus.ACTIVE ? 'Active' : 'Inactive');
      setIsConfirmed(false);
      setQuestionError('');
      setAnswerError('');
    }
  };

  const handleNext = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const questionValidation = validateQuestion(question);
    const answerValidation = validateAnswer(answer);

    setQuestionError(questionValidation);
    setAnswerError(answerValidation);

    if (questionValidation || answerValidation) {
      return;
    }

    setStep(2);
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isConfirmed) {
      showToast.error('Please confirm the information before updating.');
      return;
    }

    try {
      const response = await dispatch(
        updateFaq({
          id: item.id,
          payload: {
            question,
            answer,
            status: status === 'Active' ? FaqStatus.ACTIVE : FaqStatus.INACTIVE,
          },
        }),
      );
      if (updateFaq.fulfilled.match(response)) {
        setShowPopup(true);
      }
    } catch (error) {
      showToast.error(error as string);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!isConfirmed) {
      showToast.error('Please confirm the information before saving as draft.');
      return;
    }

    try {
      await dispatch(
        updateFaq({
          id: item.id,
          payload: { question, answer, status: FaqStatus.DRAFT },
        }),
      );
      showToast.success('FAQ saved to draft successfully!');
      navigate('/dashboard/faqs');
    } catch (error) {
      showToast.error(error as string);
    }
  };

  const handleViewList = () => {
    navigate('/dashboard/faqs');
  };

  return (
    <PageHeaderWrapper
      title="Edit FAQ"
      description="Edit the details to update the FAQ."
      onAdd={() => navigate('/dashboard/faqs')}
      addLabel="Return to FAQ List"
      showExport={false}
    >
      <Stepper
        steps={[
          {
            title: 'Basic Information',
            subtitle: 'Enter basic FAQs details',
          },
          {
            title: 'Review & Update',
            subtitle: 'Final check & confirmation',
          },
        ]}
        activeStep={step - 1}
      />

      {/* Step 1 Form */}
      {step === 1 && (
        <form onSubmit={handleNext} className="w-full bg-white rounded-xl border text-[#621C43]">
          <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
            <h2 className="text-xl font-bold">Basic Information</h2>
            <p className="text-base font-medium">Enter the basic details to update a FAQ.</p>
          </div>

          <div className="px-6 py-3 space-y-4">
            <InputField
              label="Enter the Question"
              required
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                setQuestionError(validateQuestion(e.target.value));
              }}
              placeholder="Enter the Question"
              error={questionError}
              maxLength={300}
              showCharCount
              textarea
              rows={3}
            />
            <InputField
              label="Answer"
              required
              value={answer}
              onChange={(e) => {
                setAnswer(e.target.value);
                setAnswerError(validateAnswer(e.target.value));
              }}
              placeholder="Enter the Answer" //"HTML (wysiwyg editor)"
              error={answerError}
              maxLength={600}
              showCharCount
              textarea
              rows={4}
            />
          </div>

          <div className="flex px-8 py-3 justify-between items-center">
            <button
              type="button"
              onClick={handleReset}
              className="text-[#621C43] font-medium hover:underline text-sm"
            >
              Reset
            </button>
            <button
              type="submit"
              className="bg-[#4C86E7] hover:bg-[#4C86E7] text-white px-5 py-2 rounded-lg transition"
            >
              Next →
            </button>
          </div>
        </form>
      )}

      {/* Step 2 Form */}
      {step === 2 && (
        <form onSubmit={handleUpdate} className="w-full bg-white rounded-xl border text-[#621C43]">
          {/* Header */}
          <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
            <h2 className="text-xl font-bold">Review & Update FAQ</h2>
            <p className="text-base font-medium">Review all information before updating the FAQ.</p>
          </div>

          <div className="px-5 py-4 space-y-4">
            {/* Review Box */}
            <div className="border-[0.5px] border-[#000] rounded-xl p-4">
              <div>
                <h3 className="font-medium mb-4 text-base w-full">Basic Information</h3>
              </div>

              <div className="text-sm font-medium space-y-3">
                <div className="flex justify-between items-start">
                  <span className="font-light">Question</span>
                  <span className="font-medium text-right max-w-[60%] break-words">
                    {question || '—'}
                  </span>
                </div>

                <div className="flex justify-between items-start">
                  <span className="font-light">Answer</span>
                  <span className="font-medium text-right max-w-[60%] break-words">
                    {answer || '—'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-light">Status</span>
                  <span className="inline-block text-white border border-[#4A082D] rounded-xl bg-[#851855] px-3 py-1 text-xs font-medium">
                    {status}
                  </span>
                </div>
              </div>
            </div>

            {/* Confirmation */}
            <div className="bg-[#4A082D33] border rounded-lg p-3 px-4 flex items-start">
              <input
                id="faqCheckbox"
                type="checkbox"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                className="cursor-pointer accent-[#621C43] mr-2 mt-2"
              />
              <label
                htmlFor="faqCheckbox"
                className="cursor-pointer text-sm text-[#621C43] font-medium"
              >
                <p className="mt-1">
                  I confirm that all the information provided is accurate and I want to update this
                  FAQ.
                </p>
                <p className="text-[#621C43] text-xs font-light">
                  This action will update this FAQ.
                </p>
              </label>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-between p-5">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-[#4A082D] font-medium text-md"
            >
              ← Previous
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSaveAsDraft}
                className="bg-[#4C86E7] text-white px-5 py-2 rounded-lg transition"
              >
                Save as Draft
              </button>

              <button
                type="submit"
                className="bg-[#0BA96F] text-white px-5 py-2 rounded-lg transition"
              >
                Update FAQ →
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Success Modal for Update Only */}
      {showPopup && (
        <div className="fixed right-0 bottom-0 left-[255px] top-[90px] z-50 flex items-center justify-center bg-[#E5DCE1]/80">
          <div className="relative bg-[#621C43] rounded-xl shadow-xl w-[500px] text-center border text-white">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-1 right-3 text-[#FFF8ED] hover:text-gray-300 text-3xl font-bold"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-3 pt-3 pb-3 border-b border-[#FFF8ED]">
              FAQ Updated Successfully!
            </h2>

            <div className="p-6">
              <div className="mb-4 text-sm text-center space-y-1">
                <p>
                  <strong>Q ID:</strong> {id}
                </p>
                <p>
                  <strong>Question:</strong> {question}
                </p>
              </div>

              <h4 className="text-[#FFF8ED] text-left font-medium mb-1">Status:</h4>
              <div className="bg-[#FFF8ED33] p-2 rounded-lg mb-5 shadow-sm">
                <p className="text-left text-[#FFF8ED]">✔ FAQ updated successfully.</p>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={handleViewList}
                  className="bg-[#4C86E7] hover:bg-[#4C86E7] text-[#FFF8ED] px-4 w-[250px] py-2 rounded-lg transition"
                >
                  View FAQ Lists
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageHeaderWrapper>
  );
};

export default EditFaqForm;
